import { FunctionComponent } from "react";
import styles from "./AnalyticsFilter.module.css";
import AnalyticsFilterValue from "./AnalyticsFilterValue";
import { themeColors } from "../../Components/theme";
import { connect } from "react-redux";
import "../../theme-vars.css";

/**
 * Props for AnalyticsFilter Component
 *
 * - setFilterState: Sets the active filter type (MTD, PM, All, T, Y etc.)
 * - filterUsed: Currently selected filter (string)
 * - showFilter: Controls visibility of the filter dropdown panel
 * - theme: Redux theme flag (light or dark mode)
 */
type props = {
  setFilterState: (data: "All" | "YTD" | "PM" | "MTD" | "T" | "Y") => void;
  filterUsed: string;
  showFilter: (data: boolean) => void;
  theme: any;
};

/**
 * AnalyticsFilter
 *
 * Displays the floating filter dropdown for Analytics.
 * Allows selecting:
 *  - Month Till Date
 *  - Previous Month
 *  - All
 *  - Today
 *  - Yesterday
 *
 * Applies theme-based styling and responds to selected filter.
 */
const AnalyticsFilter: FunctionComponent<props> = ({
  setFilterState,
  filterUsed,
  showFilter,
  theme
}) => {
  /**
   * Theme-based container styling
   * Uses light/dark mode colors from themeColors
   */
  const containerStyles = {
    backgroundColor: theme
      ? themeColors.dropdownBackgroundColor
      : themeColors.dropdownBackgroundColorDark,
    color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark
  };

  // CSS variables for app-wide font settings
  const fontFamilyMain = "var(--font-family-main)";
  const fontSizeSmall = "var(--font-size-small)";

  return (
    <div className={styles.parent} style={containerStyles}>
      {/* Header row with Filter title and Reset action */}
      <div className={styles.headContainer}>
        <p
          style={{
            fontFamily: fontFamilyMain,
            fontSize: fontSizeSmall,
            margin: 0
          }}
        >
          Filter Value
        </p>

        <p
          className={styles.resetText}
          style={{
            color: themeColors.danger,
            fontFamily: fontFamilyMain,
            fontSize: fontSizeSmall,
            margin: 0
          }}
          onClick={() => setFilterState("All")}
        >
          Reset
        </p>
      </div>

      {/* Filter Option: Month Till Date */}
      <AnalyticsFilterValue
        title="Month Till Date"
        setFilterState={(data) => setFilterState(data)}
        filterType="MTD"
        filterUsed={filterUsed}
        showFilter={(data) => showFilter(data)}
      />

      {/* Filter Option: Previous Month */}
      <AnalyticsFilterValue
        title="Previous Month"
        setFilterState={(data) => setFilterState(data)}
        filterType="PM"
        filterUsed={filterUsed}
        showFilter={(data) => showFilter(data)}
      />

      {/* Filter Option: All */}
      <AnalyticsFilterValue
        title="All"
        setFilterState={(data) => setFilterState(data)}
        filterType="All"
        filterUsed={filterUsed}
        showFilter={(data) => showFilter(data)}
      />

      {/* Filter Option: Today */}
      <AnalyticsFilterValue
        title="Today"
        setFilterState={(data) => setFilterState(data)}
        filterType="T"
        filterUsed={filterUsed}
        showFilter={(data) => showFilter(data)}
      />

      {/* Filter Option: Yesterday */}
      <AnalyticsFilterValue
        title="Yesterday"
        setFilterState={(data) => setFilterState(data)}
        filterType="Y"
        filterUsed={filterUsed}
        showFilter={(data) => showFilter(data)}
      />
    </div>
  );
};

/**
 * Redux Mapping
 * Gets theme mode (light/dark) from global store.
 */
const mapStateToProps = (state: any) => {
  return {
    theme: state.theme.isLightMode
  };
};

export default connect(mapStateToProps)(AnalyticsFilter);
